import React, { FC, useLayoutEffect, useRef, useState } from 'react'
import { Grid, Box, Flex } from '@moonpig/launchpad-components'
import { styled, breakpointUp } from '@moonpig/launchpad-utils'
import { ModuleTwoColumn, Module, ModuleContext } from '../types'

type Props = {
  module: ModuleTwoColumn
  context: ModuleContext
  map: (module: Module, props: ModuleContext) => React.JSX.Element | null
}

type ColumnProps = {
  ratio: string | undefined
  alignSelf: string | undefined
  height: string | undefined
  justifyContent: string | undefined
  minHeight: string | undefined
  width: string | undefined
}

type GridProps = {
  height: string | undefined
  minHeight: string | undefined
}

const calculateLeftColumnWidthPercentage = ({
  ratio,
}: {
  ratio: string | undefined
}) => {
  switch (ratio) {
    case '50/50':
      return 50
    case '40/60':
      return 40
    default:
      return 60
  }
}

const calculateRightColumnWidthPercentage = ({
  ratio,
}: {
  ratio: string | undefined
}) => {
  switch (ratio) {
    case '50/50':
      return 50
    case '40/60':
      return 60
    default:
      return 40
  }
}

const StyledLeftBlock = styled(Box)<ColumnProps>`
  width: 100%;
  ${breakpointUp('md')} {
    width: ${({ ratio }) => calculateLeftColumnWidthPercentage({ ratio })}%;
  }
`
const StyledRightBlock = styled(Box)<ColumnProps>`
  width: 100%;
  ${breakpointUp('md')} {
    width: ${({ ratio }) => calculateRightColumnWidthPercentage({ ratio })}%;
  }
`

const StyledGrid = styled(Grid)<GridProps>`
  height: ${({ height }) => height || 'auto'};
  min-height: ${({ minHeight }) => minHeight || 'auto'};
`

const StyledLeftFlex = styled(Flex)<ColumnProps>`
  width: ${({ width }) => width || '100%'};
  height: ${({ height }) => height || 'auto'};
  min-height: ${({ minHeight }) => minHeight || 'auto'};
  align-self: ${({ alignSelf }) => alignSelf || 'auto'};
  justify-content: ${({ justifyContent }) => justifyContent || 'normal'};
  ${breakpointUp('md')} {
    width: ${({ ratio }) => calculateLeftColumnWidthPercentage({ ratio })}%;
  }
`

const StyledRightFlex = styled(Flex)<ColumnProps>`
  width: ${({ width }) => width || '100%'};
  height: ${({ height }) => height || 'auto'};
  min-height: ${({ minHeight }) => minHeight || 'auto'};
  flex-direction: column;
  align-self: ${({ alignSelf }) => alignSelf || 'auto'};
  justify-content: ${({ justifyContent }) => justifyContent || 'normal'};
  ${breakpointUp('md')} {
    width: ${({ ratio }) => calculateRightColumnWidthPercentage({ ratio })}%;
  }
`

export const TwoColumnModule: FC<Props> = ({
  module: { left, right, ratio, styles },
  context,
  map,
}) => {
  const leftColumnRef = useRef<HTMLDivElement | null>(null)
  const [leftColumnHeight, setLeftColumnHeight] = useState<number>()
  const isFlexColumns = !!styles
  const StyledLeftColumn = isFlexColumns ? StyledLeftFlex : StyledLeftBlock
  const StyledRightColumn = isFlexColumns ? StyledRightFlex : StyledRightBlock
  const matchedRightHeight =
    styles?.matchRightHeightToLeft && leftColumnHeight
      ? `${leftColumnHeight}px`
      : undefined

  useLayoutEffect(() => {
    if (!styles?.matchRightHeightToLeft) {
      return undefined
    }

    const leftColumnElement = leftColumnRef.current

    /* istanbul ignore next */
    if (!leftColumnElement) {
      return undefined
    }

    const updateLeftColumnHeight = () => {
      setLeftColumnHeight(leftColumnElement.getBoundingClientRect().height)
    }

    updateLeftColumnHeight()

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateLeftColumnHeight)

      return () => {
        window.removeEventListener('resize', updateLeftColumnHeight)
      }
    }

    const resizeObserver = new ResizeObserver(updateLeftColumnHeight)
    resizeObserver.observe(leftColumnElement)

    return () => {
      resizeObserver.disconnect()
    }
  }, [styles?.matchRightHeightToLeft])

  return (
    <StyledGrid
      data-testid="module-two-column"
      gap
      height={styles?.height}
      minHeight={styles?.minHeight}
    >
      <StyledLeftColumn
        ref={leftColumnRef}
        ratio={ratio}
        alignSelf={styles?.alignSelfLeft}
        height={styles?.heightLeft}
        justifyContent={styles?.justifyContentLeft}
        minHeight={styles?.minHeightLeft}
        width={styles?.widthLeft}
      >
        {left.map((m, key) => map(m, { ...context, key }))}
      </StyledLeftColumn>
      <StyledRightColumn
        ratio={ratio}
        alignSelf={styles?.alignSelfRight}
        height={matchedRightHeight || styles?.heightRight}
        justifyContent={styles?.justifyContentRight}
        minHeight={styles?.minHeightRight}
        width={styles?.widthRight}
      >
        {right.map((m, key) => map(m, { ...context, key }))}
      </StyledRightColumn>
    </StyledGrid>
  )
}
